<template>
    <b-form-group
        :description="this.$t('uniqueIndexesDescription')"
        :label="this.$t('uniqueIndexes')"
        label-for="uniqueIndexes"
        :label-cols="2"
        :horizontal="true">
        <div class="form-actions">
            <b-button type="submit" variant="primary" @click="editor.show = true">{{ $t('edit') }}</b-button>
        </div>

        <main-dialog :title="$t('uniqueIndexes')" v-model="editor.show" size="lg">
            <div class="mb-3">
                <b-alert show variant="info" class="mb-3">
                    {{ $t('uniqueIndexesHelp') }}
                </b-alert>

                <b-list-group v-if="localIndexes.length > 0">
                    <b-list-group-item 
                        v-for="(combination, index) in localIndexes" 
                        :key="index"
                        class="d-flex justify-content-between align-items-start">
                        <div class="flex-grow-1">
                            <div class="mb-2">
                                <b-badge variant="secondary">
                                    {{ combination.schema || $t('default') }}
                                </b-badge>
                            </div>
                            <div>
                                <b-badge 
                                    v-for="(field, fIndex) in combination.fields" 
                                    :key="fIndex"
                                    variant="primary"
                                    class="mr-2">
                                    {{ getFieldLabel(field) }}
                                </b-badge>
                            </div>
                        </div>
                        <b-button 
                            variant="danger" 
                            size="sm"
                            @click="removeIndex(index)">
                            <b-icon icon="trash" />
                        </b-button>
                    </b-list-group-item>
                </b-list-group>

                <b-alert show variant="secondary" v-else>
                    {{ $t('noUniqueIndexes') }}
                </b-alert>

                <hr class="my-4" />

                <h6>{{ $t('addNewIndex') }}</h6>
                
                <b-form-group
                    :label="$t('schema')"
                    label-for="schema">
                    <v-select
                        v-model="newIndex.schema"
                        :options="availableSchemas"
                        :reduce="schema => schema.value"
                        :placeholder="$t('selectSchema')"
                        :clearable="false">
                    </v-select>
                </b-form-group>

                <b-form-group
                    :label="$t('selectFields')"
                    label-for="fields">
                    <v-select
                        v-model="newIndex.fields"
                        :options="availableFieldsForSchema"
                        label="label"
                        :reduce="field => field.key"
                        multiple
                        :placeholder="$t('selectFieldsForIndex')">
                    </v-select>
                </b-form-group>

                <b-button 
                    variant="success" 
                    @click="addIndex"
                    :disabled="newIndex.schema === null || newIndex.schema === undefined || newIndex.fields.length === 0"
                    block>
                    <b-icon icon="plus" /> {{ $t('addIndex') }}
                </b-button>
            </div>

            <template #modal-footer>
                <b-button variant="primary" @click="saveIndexes">{{ $t('save') }}</b-button>
                <b-button variant="secondary" @click="editor.show = false">{{ $t('cancel') }}</b-button>
            </template>
        </main-dialog>
    </b-form-group>
</template>

<script>
import VSelect from 'vue-select'
import _ from 'lodash'

export default {
    components: {
        VSelect
    },
    props: {
        value: {
            type: Array,
            default: () => []
        },
        fields: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            editor: {
                show: false
            },
            localIndexes: [],
            newIndex: {
                schema: '',
                fields: []
            }
        }
    },
    computed: {
        availableSchemas() {
            if (!this.fields || this.fields.length === 0) {
                return ['']
            }

            const schemas = new Set()
            schemas.add('') // Add default schema
            
            this.fields.forEach(field => {
                if (field.schema) {
                    schemas.add(field.schema)
                }
            })

            return Array.from(schemas).sort().map(schema => ({
                label: schema || this.$t('default'),
                value: schema || ''
            }))
        },

        availableFieldsForSchema() {
            if (!this.fields || this.fields.length === 0) {
                return []
            }

            // Filter fields by selected schema
            const fieldsForSchema = this.fields.filter(field => {
                return (field.schema || '') === (this.newIndex.schema || '')
            })

            return fieldsForSchema.map(field => ({
                key: field.key,
                label: this.$translateLabel(field.label, field.key)
            }))
        },

        availableFields() {
            if (!this.fields || this.fields.length === 0) {
                return []
            }

            return this.fields.map(field => ({
                key: field.key,
                label: this.$translateLabel(field.label, field.key)
            }))
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(newVal) {
                if (Array.isArray(newVal)) {
                    // Migrate old format to new format if needed
                    this.localIndexes = newVal.map(item => {
                        if (Array.isArray(item)) {
                            // Old format: array of field keys
                            return {
                                schema: '',
                                fields: item
                            }
                        } else {
                            // New format: object with schema and fields
                            return {
                                schema: item.schema || '',
                                fields: item.fields || []
                            }
                        }
                    })
                } else {
                    this.localIndexes = []
                }
            }
        },
        
        'newIndex.schema'() {
            // Reset fields when schema changes
            this.newIndex.fields = []
        }
    },
    methods: {
        getFieldLabel(key) {
            const field = this.fields.find(f => f.key === key)
            if (!field) return key
            return this.$translateLabel(field.label, key)
        },

        addIndex() {
            if (!this.newIndex.schema && this.newIndex.schema !== '') {
                return
            }
            
            if (this.newIndex.fields.length === 0) {
                return
            }

            // Check for duplicates
            const isDuplicate = this.localIndexes.some(existing => {
                return existing.schema === this.newIndex.schema && 
                       _.isEqual(_.sortBy(existing.fields), _.sortBy(this.newIndex.fields))
            })

            if (isDuplicate) {
                this.$bvToast.toast(this.$t('indexAlreadyExists'), {
                    title: this.$t('error'),
                    variant: 'warning'
                })
                return
            }

            this.localIndexes.push({
                schema: this.newIndex.schema || '',
                fields: [...this.newIndex.fields]
            })
            
            this.newIndex = {
                schema: '',
                fields: []
            }
        },

        removeIndex(index) {
            this.localIndexes.splice(index, 1)
        },

        saveIndexes() {
            this.$emit('input', this.localIndexes)
            this.editor.show = false
        }
    }
}
</script>

<style scoped>
.form-actions {
    margin-bottom: 1rem;
}
</style>
